import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── Fetch Hook ── */

function useAdminFetch() {
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Dependencies explanation:
  // ✅ Included: setLoading, setRefreshing, setQuotes, setStats, setError (React setState - guaranteed stable)
  // ❌ Not included: API (module-level constant - never changes), axios (imported library - stable reference)
  // ❌ Not included: "data" (false positive - doesn't exist as standalone variable, only as qRes.data/sRes.data)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchAll = useCallback(async () => {
    try {
      setError(null);
      const [qRes, sRes] = await Promise.all([
        axios.get(`${API}/quotes`),
        axios.get(`${API}/quotes/stats`),
      ]);
      setQuotes(qRes.data);
      setStats(sRes.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed to load data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [setError, setQuotes, setStats, setLoading, setRefreshing]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const refresh = useCallback(() => {
    setRefreshing(true);
    fetchAll();
  }, [fetchAll, setRefreshing]);

  // Dependencies explanation:
  // ✅ Included: setStats, setError (React setState - guaranteed stable)
  // ❌ Not included: API (module-level constant - never changes), axios (imported library - stable reference)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refreshStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/quotes/stats`);
      setStats(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed to refresh stats");
    }
  }, [setStats, setError]);

  return { quotes, setQuotes, stats, loading, refreshing, error, setError, refresh, refreshStats };
}

/* ── Actions Hook ── */

function useAdminActions(setQuotes, setError, refreshStats) {
  // Dependencies explanation:
  // ✅ Included: setQuotes, setError, refreshStats (React setState & callback - guaranteed stable)
  // ❌ Not included: API (module-level constant - never changes), axios (imported library - stable reference)
  const updateStatus = useCallback(
    async (quoteId, newStatus) => {
      try {
        setError(null);
        await axios.patch(`${API}/quotes/${quoteId}`, { status: newStatus });
        setQuotes((prev) =>
          prev.map((q) => (q.id === quoteId ? { ...q, status: newStatus } : q))
        );
        refreshStats();
      } catch (err) {
        setError(err?.response?.data?.detail || err.message || "Failed to update status");
      }
    },
    [setQuotes, setError, refreshStats]
  );

  // Dependencies explanation:
  // ✅ Included: setQuotes, setError, refreshStats (React setState & callback - guaranteed stable)
  // ❌ Not included: API (module-level constant - never changes), axios (imported library - stable reference)
  const deleteQuote = useCallback(
    async (quoteId) => {
      try {
        setError(null);
        await axios.delete(`${API}/quotes/${quoteId}`);
        setQuotes((prev) => prev.filter((q) => q.id !== quoteId));
        refreshStats();
      } catch (err) {
        setError(err?.response?.data?.detail || err.message || "Failed to delete quote");
      }
    },
    [setQuotes, setError, refreshStats]
  );

  return { updateStatus, deleteQuote };
}

/* ── Combined Hook ── */

export function useAdminQuotes() {
  const {
    quotes, setQuotes, stats, loading, refreshing,
    error, setError, refresh, refreshStats,
  } = useAdminFetch();

  const { updateStatus, deleteQuote } = useAdminActions(setQuotes, setError, refreshStats);

  const clearError = useCallback(() => setError(null), [setError]);

  return {
    quotes, stats, loading, refreshing, error,
    refresh, updateStatus, deleteQuote, clearError,
  };
}
